// The hot bar's Toggle boxes (qa-runs#371; shared/src/rules/starts.ts): the token's Toggle rules, asked of the server
// (`TABLE_TOGGLES`, server toggleList.ts) on the table's socket, and whether each is lit, read from the toggles the
// server sent (`litToggles`). Decides nothing: a tap sends `toggle.on`/`toggle.off` (`sendToggle`) and the server
// checks the token carries it, spends the action on the token's own turn, or refuses.
import { useEffect, useState } from 'react';
import type { Socket } from 'socket.io-client';
import { TABLE_TOGGLES, parseToggleChoices, toggleWords, type ToggleChoice } from '@dragons-table/vtt-shared';
import { litToggles } from './ToggleLights';

let socket: Pick<Socket, 'emit' | 'connected'> | null = null;
const listeners = new Set<() => void>();

/** The table's socket, given by the table's state when it connects (TableState.tsx, beside `dialogs.attach`). */
export const toggleWire = {
  attach(s: Pick<Socket, 'emit' | 'connected'>) { if (socket !== s) { socket = s; listeners.forEach((l) => l()); } },
  get: () => socket,
};

const ASK_MS = 5000;
/** A token's Toggle rules; null when they cannot be read now (no socket, refused, no answer in time). */
export function fetchToggleChoices(campaignId: string, token: string, s = socket): Promise<ToggleChoice[] | null> {
  if (!s || s.connected === false) return Promise.resolve(null);
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ASK_MS);
    s.emit(TABLE_TOGGLES, { campaignId, token }, (reply: unknown) => { clearTimeout(timer); resolve(parseToggleChoices(reply)); });
  });
}

/** A token's Toggle rules for its hot bar, asked again when the socket arrives or `again` changes (its sheet, its conditions). */
export function useToggleChoices(campaignId: string, token: string, again: unknown): ToggleChoice[] {
  const [list, setList] = useState<ToggleChoice[]>([]);
  const [wire, setWire] = useState(0);
  useEffect(() => { const l = () => setWire((n) => n + 1); listeners.add(l); return () => { listeners.delete(l); }; }, []);
  useEffect(() => {
    let live = true;
    void fetchToggleChoices(campaignId, token).then((got) => { if (live && got) setList(got); });
    return () => { live = false; };
  }, [campaignId, token, wire, again]);
  return list;
}

/** A Toggle box's hot ref, and the entry id a hot ref names (null: not a Toggle box). */
export const toggleRef = (id: string): string => `toggle:${id}`;
export const toggleIdOf = (ref: string): string | null => (ref.startsWith('toggle:') ? ref.slice(7) : null);

/** Whether the token's toggle `id` is lit, as the server last said. */
export const isLitNow = (state: unknown, token: string, id: string): boolean => litToggles(state, token).some((x) => x.entry.id === id);

/** A Toggle box's hover detail: on or off, how it lasts and what a switch costs. */
export const toggleDetail = (c: ToggleChoice, lit: boolean): string => (c.use ? toggleWords(c) : `${lit ? 'On' : 'Off'} · ${toggleWords(c)}`);
